<script setup lang="ts">
/**
 * Carte d'un événement — rayon /recherche/billetterie, accueil, suggestions.
 * Présentation « vitrine » commune (VitrineCarte, Karim 2026-10-12) : date,
 * lieu et prix posés sur l'image.
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import { fuseauDe } from "~/utils/heureAssociation";
import { villeCourteLieu, type LieuPublicLu } from "#shared/utils/lieuPublic";
import { themeAffiche, typeActiviteAffiche } from "~/utils/themesAffiches";

/** Seuls les champs affichés : l'accueil passe une version allégée. */
interface EvenementCarte extends LieuPublicLu {
  slug: string;
  title: string;
  cover_image: string | null;
  start_date: string | null;
  category: string | null;
  /** Type d'activité (concerts, ateliers…) — migration 301. */
  activity_type?: string | null;
  /** Fuseau de l'association (lot 2) — Paris par défaut. */
  timezone?: string | null;
  organization_name: string;
  organization_logo: string | null;
  min_price?: number | null;
  price_count?: number;
}

const props = defineProps<{ event: EvenementCarte; priority?: boolean }>();

const { t } = useI18n();
const { formatCurrency, formatDate } = useFormatLangue();
const { formatTime } = useRechercheHelpers();

/** Type d'activité (« Concerts ») s'il est précisé, sinon le thème. */
const pastille = computed(() => {
  const e = props.event;
  if (e.activity_type && e.activity_type !== "autre") return typeActiviteAffiche(e.activity_type);
  return e.category && e.category !== "autre" ? themeAffiche(e.category) : "";
});

/**
 * « Samedi 24 octobre · 19h », à l'heure murale de l'association. L'année
 * n'apparaît que si elle diffère de l'année en cours.
 */
const ligneDate = computed(() => {
  const e = props.event;
  if (!e.start_date) return "";
  const fuseau = fuseauDe(e);
  const autreAnnee = !e.start_date.startsWith(String(new Date().getFullYear()));
  const jour = formatDate(e.start_date, {
    format: autreAnnee ? "weekday-long" : "weekday",
    timeZone: fuseau,
  });
  const heure = formatTime(e.start_date, fuseau);
  const texte = heure ? `${jour} · ${heure}` : jour;
  return texte.charAt(0).toUpperCase() + texte.slice(1);
});

/**
 * La VILLE seule, pas le nom du lieu (même règle que la carte association,
 * Karim 2026-10-16) : sur une carte, « Salle des fêtes, Saint-Denis, La
 * Réunion » se coupait et n'apprenait rien de plus que « Saint-Denis, La
 * Réunion ».
 */
const ligneLieu = computed(() => villeCourteLieu(props.event));

/**
 * Prix (Karim 2026-06-25) : aucun tarif → rien ; gratuit ; plusieurs tarifs →
 * « À partir de 12 € ».
 */
const prix = computed(() => {
  const min = props.event.min_price;
  const nombre = props.event.price_count ?? 0;
  if (nombre === 0 || min == null) return "";
  if (min === 0) return t("association.rightColumn.gratuit");
  const montant = formatCurrency(min, { currency: "EUR", decimals: Number.isInteger(min) ? 0 : 2 });
  return nombre > 1 ? `${t("association.rightColumn.aPartirDe")} ${montant}` : montant;
});
</script>

<template>
  <VitrineCarte
    :href="`/b/${event.slug}`"
    :titre="event.title"
    :image="event.cover_image"
    :pastille="pastille"
    :association-nom="event.organization_name"
    :association-logo="event.organization_logo"
    :action="$t('commun.vitrine.actionEvenement')"
    :priority="priority"
  >
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0 text-sm">
        <p v-if="ligneDate" class="flex items-center gap-1.5 text-white">
          <UIcon name="i-lucide-calendar-days" class="size-4 shrink-0" />
          <span class="truncate" style="font-weight: 600 !important">{{ ligneDate }}</span>
        </p>
        <p v-if="ligneLieu" class="mt-1 flex items-center gap-1.5 text-white/75">
          <UIcon name="i-lucide-map-pin" class="size-4 shrink-0" />
          <span class="truncate">{{ ligneLieu }}</span>
        </p>
      </div>
      <span
        v-if="prix"
        class="shrink-0 px-3 py-1.5 rounded-full bg-white text-sm text-[#0c3097]"
        style="font-weight: 700 !important"
      >
        {{ prix }}
      </span>
    </div>
  </VitrineCarte>
</template>
